import { Formik, validateYupSchema, yupToFormErrors } from "formik";
import { useEffect } from "react";
import { object, string } from "yup";

import { addToast } from "@icg360/design-system";

import { PaperlessModalForm } from "components/paperless-modal/paperless-modal-form";
import { useKeystone, usePolicyDetail } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import {
  type PolicyDetail,
  getPaperlessEnrollment,
  logException,
  trackEvent,
} from "utils";

type PaperlessModalProps = {
  show: boolean;
  onClose: () => void;
  documentsEmailAddress?: string | null;
  policyId: string;
  userDetails?: PolicyDetail["userDetails"] | null;
  userBilling?: PolicyDetail["userBilling"] | null;
};

export const PaperlessModal = ({
  show,
  onClose,
  documentsEmailAddress,
  userDetails,
  userBilling,
  policyId,
}: PaperlessModalProps) => {
  useEffect(() => {
    if (show) {
      trackEvent("Paperless Documents Options Modal Displayed");
    }
  }, [show]);

  const keystone = useKeystone();
  const { mutate: refreshPolicy } = usePolicyDetail(policyId);

  const validate = async (values) => {
    const paperlessDocumentsAndBillingSchema = object().shape({
      documentsEmail: string()
        .required("Please enter an email address.")
        .email("Please enter a valid email address."),
    });

    try {
      await validateYupSchema(values, paperlessDocumentsAndBillingSchema, true);
    } catch (error) {
      return yupToFormErrors(error);
    }
  };
  const handleSubmit = async (
    { documentsEmail }: { documentsEmail: string },
    { setSubmitting, setStatus },
  ) => {
    const { isEnrolledDocuments, isEnrolledBilling } = getPaperlessEnrollment(
      userBilling,
      userDetails,
    );

    let enrollIn = "documentsAndBilling";

    if (!isEnrolledBilling && !isEnrolledDocuments) {
      enrollIn = "documentsAndBilling";
    } else if (!isEnrolledBilling && isEnrolledDocuments) {
      enrollIn = "billing";
    } else if (isEnrolledBilling && !isEnrolledDocuments) {
      enrollIn = "documents";
    }

    trackEvent("Paperless Documents Options Enroll Button Clicked", {
      documentsEmail,
      enrollOptionSelected: enrollIn,
    });

    try {
      setSubmitting(true);

      if (!isEnrolledDocuments) {
        const updatePaperlessDocsEmailResponse =
          await keystone.updatePaperlessDocsEmail(policyId, documentsEmail);

        if (isError(updatePaperlessDocsEmailResponse)) {
          throw updatePaperlessDocsEmailResponse;
        } else {
          const paperlessDocumentsEnrollResponse =
            await keystone.enrollPaperlessDocuments(policyId);

          if (isError(paperlessDocumentsEnrollResponse)) {
            throw paperlessDocumentsEnrollResponse;
          } else {
            trackEvent("Paperless Documents Enrolled", {
              enrollmentOrigin: "Paperless Options Modal",
              email: documentsEmail,
              policyNumber: policyId,
            });
          }
        }
      }

      if (!isEnrolledBilling) {
        const updatePaperlessBillingEmailResponse =
          await keystone.updatePaperlessBillingEmail(policyId, documentsEmail);

        if (isError(updatePaperlessBillingEmailResponse)) {
          throw updatePaperlessBillingEmailResponse;
        } else {
          const enrollPaperlessBillingResponse =
            await keystone.enrollPaperlessBilling(policyId);

          if (isError(enrollPaperlessBillingResponse)) {
            throw enrollPaperlessBillingResponse;
          } else {
            trackEvent("Paperless Billing Enrolled", {
              enrollmentOrigin: "Paperless Options Modal",
              email: documentsEmail,
              policyNumber: policyId,
            });
          }
        }
      }

      await refreshPolicy();

      onClose();

      addToast("You're in! All communications will be sent via email.", {
        icon: true,
      });
    } catch (err) {
      trackEvent("Error Displayed (Paperless Options Modal)", err);
      const [, ...lastError] = err.message.split("error: ");
      setStatus(lastError);
      logException(err);
    } finally {
      setSubmitting(false);
      await refreshPolicy();
    }
  };

  const initialValues = {
    documentsEmail: documentsEmailAddress ?? "",
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
      enableReinitialize
    >
      <PaperlessModalForm show={show} onClose={onClose} />
    </Formik>
  );
};
